import React, { useState } from 'react';
import { authAPI, formatPhoneNumber } from '../../services/api';

interface ForgotPasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}

type Step = 'phone' | 'otp' | 'newPassword' | 'success';

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate phone
    const phoneRegex = /^(\+27|0)[0-9]{9}$/;
    if (!phoneRegex.test(phone.trim())) {
      setError('Please enter a valid South African phone number');
      return;
    }

    setLoading(true);
    try {
      await authAPI.forgotPassword(formatPhoneNumber(phone.trim()));
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    // Move to password step (we'll verify OTP when setting password)
    setStep('newPassword');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(newPassword)) {
      setError('Password must contain at least one lowercase letter, one uppercase letter, and one number');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({
        phone_number: formatPhoneNumber(phone.trim()),
        verification_code: otp,
        new_password: newPassword
      });
      setStep('success');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(formatPhoneNumber(phone.trim()));
      setError(''); // Clear any previous errors
      alert('A new verification code has been sent to your phone');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-form">
      <div className="form-header">
        {step !== 'success' && (
          <button type="button" className="back-link" onClick={onBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Login
          </button>
        )}
        
        {step === 'phone' && (
          <>
            <h1>Forgot Password</h1>
            <p>Enter your phone number and we'll send you a verification code</p>
          </>
        )}
        
        {step === 'otp' && (
          <>
            <h1>Enter Verification Code</h1>
            <p>We've sent a 6-digit code to {phone}</p>
          </>
        )}
        
        {step === 'newPassword' && (
          <>
            <h1>Create New Password</h1>
            <p>Enter your new password below</p>
          </>
        )}
        
        {step === 'success' && (
          <>
            <div className="success-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Password Reset!</h1>
            <p>Your password has been successfully reset</p>
          </>
        )}
      </div>

      {error && (
        <div className="error-message general-error">
          {error}
        </div>
      )}

      {step === 'phone' && (
        <form onSubmit={handleRequestOtp} className="auth-form">
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0123456789"
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? (
              <span className="loading-spinner">
                <span className="dot"></span>
              </span>
            ) : (
              'Send Verification Code'
            )}
          </button>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={handleVerifyOtp} className="auth-form">
          <div className="form-group">
            <label htmlFor="otp">Verification Code</label>
            <input
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              disabled={loading}
              className="otp-input"
              style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || otp.length !== 6}>
            Verify Code
          </button>

          <div className="resend-section">
            <span>Didn't receive the code? </span>
            <button type="button" className="link-button" onClick={handleResendOtp} disabled={loading}>
              Resend Code
            </button>
          </div>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={handleResetPassword} className="auth-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M21 4L3 20"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <span className="helper-text">Min 8 chars, 1 uppercase, 1 lowercase, 1 number</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? (
              <span className="loading-spinner">
                <span className="dot"></span>
              </span>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      )}

      {step === 'success' && (
        <div className="auth-form">
          <button type="button" className="btn btn-primary btn-full" onClick={onSuccess}>
            Continue to Login
          </button>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;

